<template>
  <div class="cat-account">
    <div class="account-area">
      <!-- logged in -->
      <div v-if="user && !user.guest" class="logged-in">
        <!--<PortalTarget name="account-end" multiple slim />-->
        <div id="account-end" class="user-actions">
          <div
            v-if="getSupportedLocales.length > 1 && showLanguageSelector"
            class="user-actions-item"
          >
            <cat-language-selector
              :value="getLocale"
              :locales="getSupportedLocales"
              :supported-locales="getSupportedLocalesConstraint"
            />
          </div>

          <template v-if="account && account.length">
            <div
              class="user-actions-item user-actions-profile mobile-menu-fullwidth"
            >
              <inline-menu
                :html="true"
                :label="
                  organisationIdentifier
                    ? workingAs + ' - ' + user.fullName
                    : workingAs
                "
                :offscreen-label="
                  organisationIdentifier
                    ? resolveTxt('skin.account.organisation', 'Organisation') +
                      ' -'
                    : resolveTxt('skin.account.individual', 'Individual') + ' -'
                "
                :right="true"
                :class="{
                  'user-actions-menu': true,
                  'user-org': !!organisationIdentifier
                }"
              >
                <template #trigger>
                  <div class="flex-container flex-align-center-middle">
                    <cat-icon
                      v-if="!organisationIdentifier"
                      :decorative="false"
                      icon="user-circle-2"
                      class="userIconMenu"
                      :icon-label="
                        resolveTxt('skin.account.individual', 'Individual')
                      "
                    >
                      <img
                        v-if="user.avatar && user.avatar.location"
                        :src="user.avatar.location"
                      />
                    </cat-icon>
                    <cat-icon
                      v-else
                      :decorative="false"
                      icon="users-circle-2"
                      class="userIconMenu"
                      :icon-label="
                        resolveTxt('skin.account.organisation', 'Organisation')
                      "
                    />

                    <span class="truncate" style="max-width: 230px"
                      >{{ workingAs }}
                      {{ organisationIdentifier ? " - " + user.fullName : "" }}
                    </span>
                    <span></span>
                  </div>
                </template>

                <div v-if="view" class="current-user padding-around">
                  <div class="flex-container flex-align-top">
                    <cat-icon
                      v-if="!organisationIdentifier"
                      :decorative="false"
                      icon="user-circle-2"
                      class="userIcon"
                      :icon-label="
                        resolveTxt('skin.account.individual', 'Individual')
                      "
                    />
                    <cat-icon
                      v-else
                      :decorative="false"
                      icon="users-circle-2"
                      class="userIcon"
                      :icon-label="
                        resolveTxt('skin.account.organisation', 'Organisation')
                      "
                    />
                    <div class="flex-child-auto text-align-left">
                      <strong
                        class="user-profile-heading"
                        data-label-type="sub-heading"
                      >
                        {{ workingAs }}
                      </strong>
                      <div
                        v-if="!organisationIdentifier"
                        class="user-profile-heading-small"
                      >
                        {{ resolveTxt("skin.account.personal", "Personal") }}
                      </div>
                      <div
                        v-if="organisationIdentifier"
                        class="user-profile-heading-small"
                      >
                        {{ user.fullName }}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="
                    user.organisations &&
                    allowOrganisationChange &&
                    displayMenus
                  "
                  class="organisations"
                >
                  <div class="padding-around">
                    <h6 class="mn">Switch Profile</h6>
                    <span class="text-size-small">{{
                      resolveTxt(
                        "skin.account.switch.to",
                        "(Use the register as)"
                      )
                    }}</span>
                  </div>
                  <ul
                    class="prof-list"
                    role="group"
                    :aria-label="
                      resolveTxt(
                        'skin.account.switch.to',
                        'Use the register as'
                      )
                    "
                  >
                    <li :class="{ 'bg-success': !!!organisationIdentifier }">
                      <a
                        v-if="organisationIdentifier"
                        role="button"
                        tabindex="0"
                        :data-label="
                          user.fullName +
                          ' ' +
                          resolveTxt('skin.account.personal', '(Personal)')
                        "
                        @click="updateOrganisation(null)"
                        @keydown.enter="updateOrganisation(null)"
                        @keydown.space.prevent="updateOrganisation(null)"
                      >
                        <div class="flex-container flex-align-middle">
                          <cat-icon
                            :decorative="false"
                            icon="user-circle-2"
                            class="userIconMenu"
                            :icon-label="
                              resolveTxt(
                                'skin.account.individual',
                                'Individual'
                              )
                            "
                          />
                          <div class="flex-child-auto text-align-left">
                            {{ user.fullName }}
                            {{
                              resolveTxt("skin.account.personal", " (Personal)")
                            }}
                          </div>
                        </div>
                      </a>
                      <div v-else class="padding-around">
                        <div class="flex-container flex-align-middle">
                          <cat-icon
                            :decorative="false"
                            icon="user-circle-2"
                            class="userIconMenu"
                            :icon-label="
                              resolveTxt(
                                'skin.account.individual',
                                'Individual'
                              )
                            "
                          />
                          <div class="flex-child-auto text-align-left">
                            {{ user.fullName }}
                            {{
                              resolveTxt("skin.account.personal", " (Personal)")
                            }}
                          </div>
                          <span
                            class="cat-pill success font-weight-bold right mx"
                            style="font-size: 10px"
                            >{{ resolveTxt("skin.account.active", "ACTIVE") }}
                          </span>
                        </div>
                      </div>
                    </li>
                    <li
                      v-for="(org, key) in userOrganisations"
                      :key="key"
                      :class="{
                        'bg-success':
                          !!organisationIdentifier &&
                          organisationIdentifier === org.identifier
                      }"
                    >
                      <a
                        v-if="organisationIdentifier !== org.identifier"
                        role="button"
                        tabindex="0"
                        :data-label="org.name"
                        @click="updateOrganisation(org.identifier)"
                        @keydown.enter="updateOrganisation(org.identifier)"
                        @keydown.space.prevent="
                          updateOrganisation(org.identifier)
                        "
                      >
                        <div class="flex-container flex-align-middle">
                          <cat-icon
                            :decorative="false"
                            icon="users-circle-2"
                            class="userIconMenu"
                            :icon-label="
                              resolveTxt(
                                'skin.account.organisation',
                                'Organisation'
                              )
                            "
                          />
                          <div class="flex-child-auto text-align-left">
                            {{ org.name }}
                          </div>
                        </div>
                      </a>
                      <div v-else class="padding-around">
                        <div class="flex-container flex-align-middle">
                          <cat-icon
                            :decorative="false"
                            icon="users-circle-2"
                            class="userIconMenu"
                            :icon-label="
                              resolveTxt(
                                'skin.account.organisation',
                                'Organisation'
                              )
                            "
                          />
                          <div class="flex-child-auto text-align-left">
                            {{ org.name }}
                          </div>
                          <span
                            class="cat-pill success font-weight-bold right mx"
                            style="font-size: 10px"
                            >{{
                              resolveTxt("skin.account.active", "ACTIVE")
                            }}</span
                          >
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
                <ul
                  v-if="actions && actions.group && displayMenus"
                  class="ptt pbt"
                >
                  <li>
                    <a
                      :href="view.href"
                      class="pls ptt pbt prs"
                      :data-button="view.cap"
                      :data-label="view.cap"
                      >{{ view.cap }}</a
                    >
                  </li>
                  <li v-for="(action, idx) in actions.group" :key="idx + 1">
                    <a
                      :href="action.href"
                      class="pls ptt pbt prs"
                      :data-label="action.cap"
                      >{{ action.cap }}</a
                    >
                  </li>
                </ul>
              </inline-menu>
            </div>
            <div class="user-actions-item user-actions-simple">
              <cat-btn
                v-if="logoff"
                :url="confirmExitService ? null : logoff.href"
                class="logmeout"
                :link="true"
                :small="true"
                :label="logoff.cap"
                @ui-click="onLogoff(logoff.href)"
              />
            </div>
          </template>

          <template v-else>
            <div class="user-actions-item">
              <p class="my-details pbn">{{ user.fullName }}</p>
            </div>
            <div class="user-actions-item">
              <cat-btn
                v-if="logoff"
                :url="confirmExitService ? null : logoff.href"
                class="logmeout"
                :link="true"
                :small="true"
                :label="logoff.cap"
                @ui-click="onLogoff(logoff.href)"
              ></cat-btn>
            </div>
          </template>
        </div>
        <span
          v-if="lastLoginEnabled && lastLoginDate.date"
          class="user-last-login"
          ><span
            class="label"
            :data-label="resolveTxt('skin.account.lastLogin', 'Last login: ')"
            >{{ resolveTxt("skin.account.lastLogin", "Last login: ") }} </span
          ><span class="value">{{ lastLoginDate.timeText }}</span>
        </span>
      </div>

      <!-- not logged in -->
      <div v-if="!user || user.guest" class="not-logged-in">
        <!--<PortalTarget name="account-end" multiple slim />-->
        <div id="account-end" class="user-actions">
          <div
            v-if="getSupportedLocales.length > 1 && showLanguageSelector"
            class="user-actions-item"
          >
            <cat-language-selector
              :value="getLocale"
              :locales="getSupportedLocales"
              :supported-locales="getSupportedLocalesConstraint"
            />
          </div>

          <div v-if="login" class="user-actions-item user-actions-login">
            <cat-btn
              :href="login.href"
              :small="true"
              :link="true"
              :simple="true"
              class="logmein"
              :data-button="login.cap"
              :data-label="login.cap"
            >
              <cat-icon
                icon="user-option"
                class="user user-actions-login-icon"
              />
              <span>{{ login.cap }}</span>
            </cat-btn>
          </div>

          <div class="user-actions-item user-actions-register">
            <a
              v-if="create"
              :href="create.href"
              :data-button="create.cap"
              :data-label="create.cap"
            >
              {{ create.cap }}
            </a>
          </div>
          <template v-if="customItems">
            <template v-for="customItem in customItems" :key="customItem.cap">
              <div
                class="user-actions-item user-actions-custom"
                :class="findInValues(customItem.values, 'displayOptions')"
              >
                <a
                  :href="customItem.href"
                  :data-button="customItem.cap"
                  :data-label="customItem.cap"
                  :target="findInValues(customItem.values, 'target')"
                  ><span class="custom-link-text">{{ customItem.cap }}</span></a
                >
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject } from "vue";
import CatBtn from "@/components/catBtn/catBtn.vue";
import InlineMenu from "@/components/catMenu/catMenu";
import CatIcon from "@/components/utilities/catIcon.vue";
import CatLanguageSelector from "@/components/catPageTemplate/language-selector.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt");
    const getService = inject("getService");
    const getMenu = inject("getMenu");
    const accountMenu = getMenu("account");
    const findMenu = (accountMenu, value, wholeMenu) => {
      const menu = accountMenu.filter(function (e) {
        const val = e.values
          ? e.values.filter(function (o) {
              return o.name == value;
            })
          : null;
        return val ? val.length > 0 : null;
      });
      return wholeMenu ? menu : menu[0];
    };

    const login = findMenu(accountMenu, "account_logon");
    const logoff = findMenu(accountMenu, "account_logoff");
    const create = findMenu(accountMenu, "account_create");
    const organisations = findMenu(accountMenu, "account_organisations");
    const actions = findMenu(accountMenu, "account_actions");
    const view = findMenu(accountMenu, "account_view");
    const customItems = findMenu(
      accountMenu,
      "account_custom_logon_item",
      true
    );

    return {
      resolveTxt,
      getService,
      getMenu,
      login,
      logoff,
      create,
      organisations,
      actions,
      view,
      customItems
    };
  },
  name: "CatAccount",
  components: {
    CatBtn,
    InlineMenu,
    CatIcon,
    CatLanguageSelector
  },
  props: {
    user: {
      type: [Object]
    },
    organisationIdentifier: {
      type: String,
      required: false
    },
    allowOrganisationChange: {
      type: Boolean,
      default: true
    },
    confirmExitService: {
      type: Boolean,
      default: false,
      description:
        "Indicates if redirect to the menu items and logout url. If true, the redirect is emitted to the parent to handle processing."
    },
    displayMenus: {
      type: Boolean,
      default: true,
      description:
        "Shows/hides all the menu items inside the user account dropdown."
    }
  },
  data: function () {
    const accountMenu = this.getMenu("account");

    return {
      additionalClasses: [],
      account: accountMenu,
      // login: this.findMenu(accountMenu, "account_logon"),
      // logoff: this.findMenu(accountMenu, "account_logoff"),
      // create: this.findMenu(accountMenu, "account_create"),
      // organisations: this.findMenu(accountMenu, "account_organisations"),
      // actions: this.findMenu(accountMenu, "account_actions"),
      // view: this.findMenu(accountMenu, "account_view"),
      getLocale: this.getService().locale || "en",
      getSupportedLocales: this.getService().supportedLocales || [],
      showLanguageSelector: this.getService().showLanguageSelector,
      getSupportedLocalesConstraint:
        this.getService().supportedLocalesConstraint || [],
      lastLoginEnabled: this.getService().lastLoginEnabled,
      lastLoginDate: this.getService().lastLoginDate
    };
  },
  computed: {
    workingAs: function () {
      if (this.organisationIdentifier) {
        var name = "Organisation";
        if (this.user.organisations) {
          var org = this.user.organisations[this.organisationIdentifier];
          if (org) {
            name = org.name;
          }
        }
        return name;
      }
      return this.user.fullName;
    },
    organisation: function () {
      return this.organisationIdentifier || "ME";
    },
    userOrganisations: function () {
      return Object.entries(this.user.organisations)
        .sort(([, a], [, b]) => a.name.localeCompare(b.name))
        .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});
    }
  },

  methods: {
    onLogoff: function (url) {
      if (!this.confirmExitService) {
        var showLoader = new CustomEvent("ui-block", { detail: {} });
        document.dispatchEvent(showLoader);
        return false;
      }
      this.$emit("ui-logoff", {
        url: url,
        confirmTitle: this.resolveTxt(
          "service.dialog.confirm.logout.title",
          "Are you sure you want to log out?"
        ),
        confirmText: this.resolveTxt(
          "service.dialog.confirm.logout.text",
          "You will lose your changes."
        ),
        confirmButtonConfirm: this.resolveTxt(
          "service.dialog.confirm.logout.confirm",
          "Log out"
        ),
        confirmButtonCancel: this.resolveTxt(
          "service.dialog.confirm.logout.cancel",
          "Go back"
        )
      });
    },
    toggleActions: function (evt) {
      this.actionsMenu = !this.actionsMenu;
      jQuery(".user-actions-menu").stop(true, true).toggleClass("offset");
    },
    updateOrganisation: function (newOrgId) {
      this.$emit("ui-change-organisation", { id: newOrgId });
      this.toggleActions();
    },
    findInValues: function (values, val) {
      const res =
        values && val
          ? values.find((element) => element.name === val)
          : undefined;
      return res ? res.value : undefined;
    }
  }
});
</script>
